import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Fade,
  Chip, Button, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tab
} from '@mui/material';
import {
  Users, CheckCircle2, XCircle, Clock, DollarSign,
  RefreshCw, Eye, Copy, Check, Banknote
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledTableContainer = styled(TableContainer)(() => ({
  background: '#141926',
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.06)',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#D1D5DB',
    padding: '12px 16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    background: 'rgba(124,58,237,0.04)',
  },
}));

export default function AdminAfiliados() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [copied, setCopied] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const fetchAffiliates = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/affiliates/admin`, authHeader());
      if (res.data.success) setAffiliates(res.data.affiliates);
    } catch { setError('Erro ao carregar afiliados'); }
    finally { setLoading(false); }
  }, [authHeader]);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/affiliates/admin/withdrawals`, authHeader());
      if (res.data.success) setWithdrawals(res.data.withdrawals);
    } catch { /* silently ignore */ }
  }, [authHeader]);

  useEffect(() => { fetchAffiliates(); fetchWithdrawals(); }, [fetchAffiliates, fetchWithdrawals]);

  const handleAction = async (id, action) => {
    setActionLoading(id);
    setError(''); setSuccess('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/affiliates/admin/${id}/${action}`,
        {},
        authHeader()
      );
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchAffiliates();
        if (detailOpen) setDetailOpen(false);
      } else { setError(res.data.message); }
    } catch (err) { setError(err.response?.data?.message || 'Erro na ação'); }
    finally { setActionLoading(''); }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const confirmWithdraw = async (wId) => {
    setActionLoading(wId); setError(''); setSuccess('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/affiliates/admin/withdrawals/${wId}/confirm`, {}, authHeader());
      if (res.data.success) { setSuccess('Transferência confirmada!'); fetchWithdrawals(); fetchAffiliates(); }
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || 'Erro ao confirmar'); }
    finally { setActionLoading(''); }
  };

  const pending = affiliates.filter(a => a.status === 'pending');
  const approved = affiliates.filter(a => a.status === 'approved');
  const rejected = affiliates.filter(a => a.status === 'rejected');

  const filtered = tab === 0 ? pending : tab === 1 ? approved : rejected;
  const totalEarnings = approved.reduce((sum, a) => sum + (a.totalEarnings || 0), 0);
  const totalReferrals = approved.reduce((sum, a) => sum + (a.totalReferrals || 0), 0);

  const statusColor = {
    pending: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', label: 'Pendente' },
    approved: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'Aprovado' },
    rejected: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'Rejeitado' },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress size={32} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Users size={22} color="#7C3AED" />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB' }}>
                Afiliados
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Gerencie as solicitações e afiliados ativos
            </Typography>
          </Box>
          <Tooltip title="Atualizar">
            <IconButton onClick={() => { setLoading(true); fetchAffiliates(); }} sx={{ color: '#7C3AED' }}>
              <RefreshCw size={20} />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.2)', '& .MuiAlert-icon': { color: '#EF4444' } }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10B981' } }}>{success}</Alert>}

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Pendentes', value: pending.length, icon: <Clock size={20} />, color: '#F59E0B' },
            { label: 'Aprovados', value: approved.length, icon: <CheckCircle2 size={20} />, color: '#10B981' },
            { label: 'Total Indicados', value: totalReferrals, icon: <Users size={20} />, color: '#7C3AED' },
            { label: 'Comissões Geradas', value: `R$ ${totalEarnings.toFixed(2).replace('.', ',')}`, icon: <DollarSign size={20} />, color: '#10B981' },
          ].map(s => (
            <Paper key={s.label} sx={{ flex: 1, minWidth: 140, p: 2.5, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, textAlign: 'center' }}>
              <Box sx={{ color: s.color, mb: 1 }}>{s.icon}</Box>
              <Typography sx={{ color: '#F9FAFB', fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>{s.value}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', mt: 0.5 }}>{s.label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Saques Pendentes */}
        {withdrawals.filter(w => w.status === 'pending').length > 0 && (
          <Paper sx={{ mb: 3, p: 2.5, background: '#141926', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Banknote size={20} color="#F59E0B" />
              <Typography sx={{ color: '#FCD34D', fontWeight: 700, fontSize: '1rem' }}>
                Saques Pendentes ({withdrawals.filter(w => w.status === 'pending').length})
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />
            {withdrawals.filter(w => w.status === 'pending').map(w => (
              <Box key={w._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1, borderRadius: 2, background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.12)', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ color: '#F9FAFB', fontWeight: 600, fontSize: '0.95rem' }}>{w.nome}</Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>{w.email}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.68rem', textTransform: 'uppercase' }}>Chave Pix</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ color: '#A78BFA', fontWeight: 600, fontSize: '0.85rem' }}>{w.chavePix}</Typography>
                    <Tooltip title={copied === `pix-${w._id}` ? 'Copiado!' : 'Copiar'}>
                      <IconButton size="small" onClick={() => handleCopy(w.chavePix, `pix-${w._id}`)}>
                        {copied === `pix-${w._id}` ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.68rem', textTransform: 'uppercase' }}>Valor</Typography>
                  <Typography sx={{ color: '#10B981', fontWeight: 800, fontSize: '1.1rem' }}>
                    R$ {w.valor?.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.68rem', textTransform: 'uppercase', mb: 0.5 }}>Solicitado em</Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>
                    {new Date(w.createdAt).toLocaleDateString('pt-BR')}
                  </Typography>
                </Box>
                <Button
                  variant="contained" size="small"
                  disabled={actionLoading === w._id}
                  onClick={() => confirmWithdraw(w._id)}
                  startIcon={actionLoading === w._id ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckCircle2 size={14} />}
                  sx={{ background: '#10B981', '&:hover': { background: '#059669' }, fontWeight: 600, textTransform: 'none', px: 2 }}
                >
                  Confirmar transferência
                </Button>
              </Box>
            ))}
          </Paper>
        )}

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { color: '#6B7280', textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: '#7C3AED' }, '& .MuiTabs-indicator': { backgroundColor: '#7C3AED' } }}>
          <Tab label={`Pendentes (${pending.length})`} />
          <Tab label={`Aprovados (${approved.length})`} />
          <Tab label={`Rejeitados (${rejected.length})`} />
        </Tabs>

        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Users size={40} color="#374151" />
            <Typography sx={{ color: '#6B7280', mt: 1 }}>
              Nenhum afiliado nesta categoria
            </Typography>
          </Box>
        ) : (
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>WhatsApp</TableCell>
                  <TableCell>Status</TableCell>
                  {tab === 1 && <TableCell>Indicados</TableCell>}
                  {tab === 1 && <TableCell>Ganhos</TableCell>}
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(a => {
                  const sc = statusColor[a.status];
                  return (
                    <TableRow key={a._id} sx={{ '&:hover': { background: 'rgba(124,58,237,0.04)' } }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.nome}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{a.email}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{a.whatsapp}</TableCell>
                      <TableCell>
                        <Chip label={sc.label} size="small" sx={{ background: sc.bg, color: sc.color, fontWeight: 600, fontSize: '0.72rem' }} />
                      </TableCell>
                      {tab === 1 && <TableCell sx={{ fontWeight: 600 }}>{a.totalReferrals || 0}</TableCell>}
                      {tab === 1 && <TableCell sx={{ color: '#10B981', fontWeight: 600 }}>R$ {(a.totalEarnings || 0).toFixed(2).replace('.', ',')}</TableCell>}
                      <TableCell sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Ver detalhes">
                            <IconButton size="small" onClick={() => { setSelectedAffiliate(a); setDetailOpen(true); }}
                              sx={{ color: '#A78BFA' }}>
                              <Eye size={16} />
                            </IconButton>
                          </Tooltip>
                          {a.status === 'pending' && (
                            <>
                              <Tooltip title="Aprovar">
                                <IconButton size="small" onClick={() => handleAction(a._id, 'approve')}
                                  disabled={actionLoading === a._id}
                                  sx={{ color: '#10B981' }}>
                                  {actionLoading === a._id ? <CircularProgress size={14} /> : <CheckCircle2 size={16} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Rejeitar">
                                <IconButton size="small" onClick={() => handleAction(a._id, 'reject')}
                                  disabled={actionLoading === a._id}
                                  sx={{ color: '#EF4444' }}>
                                  <XCircle size={16} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 } }}
        >
          {selectedAffiliate && (
            <>
              <DialogTitle sx={{ color: '#F9FAFB', fontWeight: 700, pb: 1 }}>
                {selectedAffiliate.nome}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { label: 'Email', value: selectedAffiliate.email },
                    { label: 'WhatsApp', value: selectedAffiliate.whatsapp },
                    { label: 'Chave Pix', value: selectedAffiliate.chavePix, copy: true },
                    { label: 'Código de Indicação', value: selectedAffiliate.refCode, copy: true },
                  ].map(item => (
                    <Box key={item.label}>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.3 }}>{item.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: '#F9FAFB', fontSize: '0.9rem', wordBreak: 'break-all' }}>{item.value}</Typography>
                        {item.copy && (
                          <Tooltip title={copied === item.label ? 'Copiado!' : 'Copiar'}>
                            <IconButton size="small" onClick={() => handleCopy(item.value, item.label)}>
                              {copied === item.label ? <Check size={14} color="#10B981" /> : <Copy size={14} color="#6B7280" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                  <Box>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', mb: 0.5 }}>Motivo</Typography>
                    <Typography sx={{ color: '#D1D5DB', fontSize: '0.88rem', whiteSpace: 'pre-wrap', p: 1.5, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      {selectedAffiliate.motivo}
                    </Typography>
                  </Box>

                  {selectedAffiliate.status === 'approved' && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1, p: 2, borderRadius: 2, background: 'rgba(124,58,237,0.06)', textAlign: 'center' }}>
                        <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.3rem' }}>{selectedAffiliate.totalReferrals || 0}</Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>Indicados</Typography>
                      </Box>
                      <Box sx={{ flex: 1, p: 2, borderRadius: 2, background: 'rgba(16,185,129,0.06)', textAlign: 'center' }}>
                        <Typography sx={{ color: '#10B981', fontWeight: 700, fontSize: '1.3rem' }}>R$ {(selectedAffiliate.totalEarnings || 0).toFixed(2).replace('.', ',')}</Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>Total Ganho</Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.78rem' }}>Status:</Typography>
                    <Chip label={statusColor[selectedAffiliate.status].label} size="small"
                      sx={{ background: statusColor[selectedAffiliate.status].bg, color: statusColor[selectedAffiliate.status].color, fontWeight: 600 }} />
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.06)', gap: 1 }}>
                {selectedAffiliate.status === 'pending' && (
                  <>
                    <Button onClick={() => handleAction(selectedAffiliate._id, 'reject')}
                      disabled={actionLoading === selectedAffiliate._id}
                      sx={{ color: '#EF4444', textTransform: 'none' }} startIcon={<XCircle size={14} />}>
                      Rejeitar
                    </Button>
                    <Button variant="contained" onClick={() => handleAction(selectedAffiliate._id, 'approve')}
                      disabled={actionLoading === selectedAffiliate._id}
                      sx={{ background: '#10B981', textTransform: 'none', '&:hover': { background: '#059669' } }}
                      startIcon={actionLoading === selectedAffiliate._id ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckCircle2 size={14} />}>
                      Aprovar
                    </Button>
                  </>
                )}
                <Button onClick={() => setDetailOpen(false)} sx={{ color: '#9CA3AF', textTransform: 'none' }}>Fechar</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Fade>
  );
}
